import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import crmApi, { money, STAGE_META, STAGE_ORDER } from '../../utils/crmApi';
import useLiveRefresh from '../../utils/useLiveRefresh';

const BRAND = '#0D9488';

function ringColor(pct) { return pct >= 80 ? '#16A34A' : pct >= 50 ? '#D97706' : '#DC2626'; }
function Ring({ pct, size = 58 }) {
  const r = (size - 7) / 2, c = 2 * Math.PI * r, off = c - (Math.min(100, pct) / 100) * c;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--surface2)" strokeWidth="5.5" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={ringColor(pct)} strokeWidth="5.5" strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" />
    </svg>
  );
}

function StatCard({ icon, label, value, sub, accent, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <div onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} className="card"
      style={{ padding: '16px', cursor: onClick ? 'pointer' : 'default', transition: 'transform .12s, box-shadow .12s, border-color .12s',
        transform: hover && onClick ? 'translateY(-2px)' : 'none', boxShadow: hover && onClick ? '0 6px 20px rgba(16,24,42,0.10)' : undefined,
        borderColor: hover && onClick ? accent : undefined }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '10px' }}>
        <div style={{ width: 30, height: 30, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: accent + '18', color: accent, flexShrink: 0 }}>{icon}</div>
        <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px' }}>{label}</div>
      </div>
      <div style={{ fontSize: '24px', fontWeight: '800', color: 'var(--text)', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '5px' }}>{sub}</div>}
    </div>
  );
}

function Hero({ label, value, sub, gradient, accent }) {
  return (
    <div className="card" style={{ padding: '18px', ...(gradient ? { background: gradient, color: '#fff' } : {}) }}>
      <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '.5px', opacity: gradient ? 0.9 : 1, color: gradient ? '#fff' : 'var(--muted)' }}>{label}</div>
      <div style={{ fontSize: '26px', fontWeight: '800', lineHeight: 1.1, marginTop: '6px', color: gradient ? '#fff' : (accent || 'var(--text)') }}>{value}</div>
      {sub && <div style={{ fontSize: '11px', marginTop: '4px', opacity: gradient ? 0.9 : 1, color: gradient ? '#fff' : 'var(--muted)' }}>{sub}</div>}
    </div>
  );
}

const dot = (color) => <span style={{ width: 11, height: 11, borderRadius: '50%', background: color, display: 'block' }} />;
const Ic = {
  deal: <svg width="16" height="16" viewBox="0 0 18 18" fill="none"><path d="M2 4.5h14M2 9h9M2 13.5h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
  acct: <svg width="16" height="16" viewBox="0 0 18 18" fill="none"><rect x="3" y="2.5" width="12" height="13" rx="1.4" stroke="currentColor" strokeWidth="1.5"/></svg>,
  box: <svg width="16" height="16" viewBox="0 0 18 18" fill="none"><path d="M9 2l6 3.2v7.6L9 16l-6-3.2V5.2L9 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
  doc: <svg width="16" height="16" viewBox="0 0 18 18" fill="none"><path d="M4 2h6l4 4v10H4V2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
};

export default function CrmDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const navigate = useNavigate();
  const nowY = new Date().getFullYear();
  const [year, setYear] = useState('all');
  const [showLost, setShowLost] = useState(false);

  const loadDash = useCallback(() => {
    const q = year === 'all' ? '' : `?year=${year}`;
    crmApi.get(`/dashboard${q}`).then(setData).catch(e => setErr(e?.error || 'Failed to load'));
  }, [year]);
  useEffect(() => {
    setData(null); setErr(null);
    loadDash();
  }, [loadDash]);
  useLiveRefresh(loadDash);
  if (err) return <div style={{ color: 'var(--red)' }}>{err}</div>;
  if (!data) return <div style={{ color: 'var(--muted)' }}>Loading dashboard…</div>;

  const c = data.counts || {};
  const openStages = STAGE_ORDER.filter(s => !['won', 'lost'].includes(s));
  const openCount = openStages.reduce((s, st) => s + (data.pipeline[st]?.count || 0), 0);
  const maxStageVal = Math.max(1, ...STAGE_ORDER.map(s => data.pipeline[s]?.value || 0));
  const monthly = data.finance?.monthly || [];
  const maxMonth = Math.max(1, ...monthly.map(m => m.revenue));
  const sect = { fontSize: '12px', fontWeight: '700', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: '10px' };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: '21px', fontWeight: '800', marginBottom: '3px' }}>Dashboard</h1>
          <p style={{ fontSize: '13px', color: 'var(--muted)' }}>A live overview of your sales, pipeline, goals and inventory.</p>
        </div>
        <select className="select" style={{ width: 130 }} value={year} onChange={e => setYear(e.target.value)}>
          <option value="all">All years</option>
          {Array.from({ length: (2035 - 2022) + 1 }, (_, i) => 2035 - i).map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* Hero */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', marginBottom: '24px' }}>
        <Hero label="Win Rate" value={`${data.win_rate}%`} sub={`${data.pipeline.won?.count || 0} won · ${data.pipeline.lost?.count || 0} lost`} gradient="linear-gradient(135deg, #0D9488, #0B7A70)" />
        <Hero label="Open Pipeline (Sales)" value={money(data.open_value)} sub={`Estimate ${money(data.open_estimate || 0)} · ${openCount} active deals`} accent={BRAND} />
        <Hero label="Revenue (Confirmed)" value={money(data.finance?.revenue || 0)} sub="from confirmed service orders" accent="#16A34A" />
        <Hero label="Gross Margin" value={money(data.finance?.gross_margin || 0)} sub={`${data.finance?.margin_pct_sale || 0}% of confirmed sales`} accent="#0D9488" />
      </div>

      {/* Deal outcomes */}
      {data.deal_outcomes && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', marginBottom: '24px' }}>
          <StatCard icon={dot('#16A34A')} label="Deals Won" value={`${data.pipeline.won?.count || 0}`} sub={`Revenue ${money(data.deal_outcomes.won_revenue)}`} accent="#16A34A" onClick={() => navigate('/crm/pipeline')} />
          <StatCard icon={dot('#16A34A')} label="Won Sales Value" value={money(data.deal_outcomes.won_gross)} sub={`Margin ${money(data.deal_outcomes.won_revenue)}`} accent="#16A34A" onClick={() => navigate('/crm/pipeline')} />
          <StatCard icon={dot('#DC2626')} label="Deals Lost" value={`${data.pipeline.lost?.count || 0}`} sub={`Missed revenue ${money(data.deal_outcomes.lost_revenue)} · why?`} accent="#DC2626" onClick={() => setShowLost(true)} />
          <StatCard icon={dot('#DC2626')} label="Lost Sales Value" value={money(data.deal_outcomes.lost_gross)} sub={`Missed margin ${money(data.deal_outcomes.lost_revenue)}`} accent="#DC2626" onClick={() => navigate('/crm/pipeline')} />
        </div>
      )}

      {/* Sales pipeline cards */}
      <div style={sect}>Sales Pipeline</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px', marginBottom: '24px' }}>
        {STAGE_ORDER.map(s => {
          const v = data.pipeline[s] || { count: 0, value: 0 };
          const meta = STAGE_META[s];
          return <StatCard key={s} icon={dot(meta.color)} label={meta.label} value={v.count} sub={money(v.value)} accent={meta.color} onClick={() => navigate('/crm/pipeline')} />;
        })}
      </div>

      {/* Business at a glance */}
      <div style={sect}>Business at a Glance</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '24px' }}>
        <StatCard icon={Ic.deal} label="Total Deals" value={c.deals || 0} sub={`${openCount} open`} accent={BRAND} onClick={() => navigate('/crm/pipeline')} />
        <StatCard icon={Ic.acct} label="Accounts" value={c.accounts || 0} sub="Companies" accent="#2563EB" onClick={() => navigate('/crm/accounts')} />
        <StatCard icon={Ic.doc} label="Quotes" value={c.quotes || 0} sub="Price quotes" accent="#0891B2" onClick={() => navigate('/crm/quotes')} />
        <StatCard icon={Ic.doc} label="Service Orders" value={c.sales_orders || 0} sub="Confirmed" accent="#D97706" onClick={() => navigate('/crm/sales-orders')} />
        <StatCard icon={Ic.doc} label="Invoices" value={c.invoices || 0} sub="Billing" accent="#16A34A" onClick={() => navigate('/crm/invoices')} />
      </div>

      {/* Inventory */}
      <div style={sect}>Inventory</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '24px' }}>
        <StatCard icon={Ic.box} label="Products" value={data.inventory?.product_count || 0} sub="Active" accent="#7C3AED" onClick={() => navigate('/crm/inventory')} />
        <StatCard icon={Ic.box} label="Categories" value={data.inventory?.category_count || 0} sub="Groups" accent="#0891B2" onClick={() => navigate('/crm/categories')} />
        <StatCard icon={Ic.box} label="Units in Stock" value={data.inventory?.total_units || 0} sub="All products" accent="#0D9488" onClick={() => navigate('/crm/stock')} />
        <StatCard icon={Ic.box} label="Stock Value" value={money(data.inventory?.stock_sale_value).replace('OMR ', '')} sub="At sale price" accent="#16A34A" onClick={() => navigate('/crm/stock')} />
        <StatCard icon={Ic.box} label="Stock Cost" value={money(data.inventory?.stock_cost_value).replace('OMR ', '')} sub="At cost" accent="#D97706" onClick={() => navigate('/crm/stock')} />
      </div>

      {/* Goals (after inventory) */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
        <div style={sect}>Goals & Targets</div>
        <button className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={() => navigate('/crm/goals')}>Manage goals</button>
      </div>
      {(!data.goals || data.goals.length === 0) ? (
        <div className="card" style={{ padding: '16px', marginBottom: '24px', fontSize: '13px', color: 'var(--muted)' }}>
          No active goals. <span style={{ color: BRAND, cursor: 'pointer', fontWeight: 600 }} onClick={() => navigate('/crm/goals')}>Set a target</span> to track progress here.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px', marginBottom: '24px' }}>
          {data.goals.map(g => (
            <div key={g.id} className="card" style={{ padding: '16px', display: 'flex', gap: '14px', alignItems: 'center', cursor: 'pointer' }} onClick={() => navigate('/crm/goals')}>
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <Ring pct={g.pct} />
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '800' }}>{g.pct}%</div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: '800', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.title || g.metric_label}</div>
                <div style={{ fontSize: '12px', color: 'var(--muted2)', marginTop: '2px' }}>{g.is_money ? money(g.actual) : g.actual} / {g.is_money ? money(g.target) : g.target}</div>
                <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '3px' }}>{g.scope_label} · {g.period} · {g.days_left}d left</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sales Forecast snapshot */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
        <div style={sect}>Sales Forecast · {data.sales_forecast?.year || ''}</div>
        <button className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={() => navigate('/crm/forecast')}>Open forecast</button>
      </div>
      {!data.sales_forecast?.has_active ? (
        <div className="card" style={{ padding: '16px', marginBottom: '24px', fontSize: '13px', color: 'var(--muted)' }}>
          No forecast figures yet. <span style={{ color: BRAND, cursor: 'pointer', fontWeight: 600 }} onClick={() => navigate('/crm/forecast')}>Build the Annual Target Forecast</span> to see targets vs actuals here.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '14px', marginBottom: '24px' }}>
          <div className="card" style={{ padding: '16px', cursor: 'pointer' }} onClick={() => navigate('/crm/forecast')}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--muted)', textTransform: 'uppercase' }}>Target Revenue</div>
            <div style={{ fontSize: '20px', fontWeight: '800', marginTop: '4px', color: '#2563EB' }}>{money(data.sales_forecast.target_revenue)}</div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>{data.sales_forecast.target_name}</div>
          </div>
          <div className="card" style={{ padding: '16px', cursor: 'pointer' }} onClick={() => navigate('/crm/forecast')}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--muted)', textTransform: 'uppercase' }}>Actual Revenue</div>
            <div style={{ fontSize: '20px', fontWeight: '800', marginTop: '4px', color: '#16A34A' }}>{money(data.sales_forecast.actual_revenue)}</div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>from sales so far</div>
          </div>
          <div className="card" style={{ padding: '16px', cursor: 'pointer' }} onClick={() => navigate('/crm/forecast')}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--muted)', textTransform: 'uppercase' }}>Attainment</div>
            <div style={{ fontSize: '20px', fontWeight: '800', marginTop: '4px', color: data.sales_forecast.attainment >= 80 ? '#16A34A' : data.sales_forecast.attainment >= 50 ? '#D97706' : '#DC2626' }}>{data.sales_forecast.attainment}%</div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>actuals vs target</div>
          </div>
          <div className="card" style={{ padding: '16px', cursor: 'pointer' }} onClick={() => navigate('/crm/forecast')}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--muted)', textTransform: 'uppercase' }}>Gap to Target</div>
            <div style={{ fontSize: '20px', fontWeight: '800', marginTop: '4px', color: '#7C3AED' }}>{money(Math.max(0, data.sales_forecast.target_revenue - data.sales_forecast.actual_revenue))}</div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>remaining to hit plan</div>
          </div>
        </div>
      )}

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', alignItems: 'start' }}>
        <div className="card" style={{ padding: '18px' }}>
          <div style={{ fontSize: '14px', fontWeight: '800', marginBottom: '14px' }}>Pipeline by Stage</div>
          {STAGE_ORDER.map(s => {
            const v = data.pipeline[s] || { count: 0, value: 0 };
            const meta = STAGE_META[s];
            return (
              <div key={s} style={{ marginBottom: '12px', cursor: 'pointer' }} onClick={() => navigate('/crm/pipeline')}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                  <span style={{ fontWeight: '700', color: meta.color }}>{meta.label} <span style={{ color: 'var(--muted)', fontWeight: '500' }}>({v.count})</span></span>
                  <span style={{ fontWeight: '700' }}>{money(v.value)}</span>
                </div>
                <div style={{ height: '8px', background: 'var(--surface2)', borderRadius: '99px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${(v.value / maxStageVal) * 100}%`, background: meta.color, borderRadius: '99px' }} />
                </div>
              </div>
            );
          })}
          <div style={{ marginTop: '16px', padding: '12px', background: 'var(--surface2)', borderRadius: 'var(--radius)' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--muted)', textTransform: 'uppercase' }}>Weighted Forecast</div>
            <div style={{ display: 'flex', gap: '18px', marginTop: '6px', flexWrap: 'wrap' }}>
              <div><div style={{ fontSize: '16px', fontWeight: '800', color: BRAND }}>{money(data.forecast?.weighted_all || 0)}</div><div style={{ fontSize: '10px', color: 'var(--muted)' }}>All open</div></div>
              <div><div style={{ fontSize: '16px', fontWeight: '800' }}>{money(data.forecast?.weighted_quarter || 0)}</div><div style={{ fontSize: '10px', color: 'var(--muted)' }}>This quarter</div></div>
              <div><div style={{ fontSize: '16px', fontWeight: '800' }}>{money(data.forecast?.weighted_year || 0)}</div><div style={{ fontSize: '10px', color: 'var(--muted)' }}>This year</div></div>
            </div>
            <div style={{ fontSize: '10px', color: 'var(--muted)', marginTop: '6px' }}>Expected to close, weighted by each deal's probability.</div>
          </div>
        </div>

        <div className="card" style={{ padding: '18px' }}>
          <div style={{ fontSize: '14px', fontWeight: '800', marginBottom: '14px' }}>Confirmed Sales Trend</div>
          {monthly.length === 0 ? <div style={{ fontSize: '12px', color: 'var(--muted)' }}>No confirmed sales yet.</div> : (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '170px', paddingTop: '10px' }}>
              {monthly.map(m => (
                <div key={m.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                  <div style={{ fontSize: '9px', color: 'var(--muted)', marginBottom: '3px' }}>{m.revenue >= 1000 ? (m.revenue / 1000).toFixed(0) + 'k' : Math.round(m.revenue)}</div>
                  <div style={{ width: '100%', maxWidth: '34px', height: `${(m.revenue / maxMonth) * 100}%`, minHeight: '2px', background: BRAND, borderRadius: '4px 4px 0 0' }} />
                  <div style={{ fontSize: '9px', color: 'var(--muted)', marginTop: '4px' }}>{m.month.slice(5)}</div>
                </div>
              ))}
            </div>
          )}
          <div style={{ marginTop: '14px', display: 'flex', gap: '16px', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
            <div><div style={{ fontSize: '15px', fontWeight: '800', color: '#7C3AED' }}>{money(data.finance?.recurring_revenue || 0)}</div><div style={{ fontSize: '10px', color: 'var(--muted)' }}>Recurring / mo</div></div>
            <div><div style={{ fontSize: '15px', fontWeight: '800' }}>{money(data.finance?.onetime_revenue || 0)}</div><div style={{ fontSize: '10px', color: 'var(--muted)' }}>One-time</div></div>
            <div><div style={{ fontSize: '15px', fontWeight: '800', color: '#D97706' }}>{money(data.revenue.outstanding)}</div><div style={{ fontSize: '10px', color: 'var(--muted)' }}>Outstanding</div></div>
          </div>
        </div>
      </div>

      {showLost && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setShowLost(false); }}>
          <div className="modal" style={{ width: '480px', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '14px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '800', flex: 1 }}>Why deals were lost</h3>
              <button className="btn btn-sm" onClick={() => setShowLost(false)}>Close</button>
            </div>
            {(!data.deal_outcomes?.lost_reasons || data.deal_outcomes.lost_reasons.length === 0) ? (
              <div style={{ fontSize: '13px', color: 'var(--muted)', padding: '12px 0' }}>No lost deals{year !== 'all' ? ` in ${year}` : ''} yet.</div>
            ) : (
              <div>
                {(() => {
                  const total = data.deal_outcomes.lost_reasons.reduce((s, r) => s + r.count, 0);
                  return data.deal_outcomes.lost_reasons.map((r, i) => {
                    const pctv = total > 0 ? Math.round(r.count / total * 100) : 0;
                    return (
                      <div key={i} style={{ marginBottom: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                          <span style={{ fontWeight: '600' }}>{r.reason}</span>
                          <span style={{ color: 'var(--muted)' }}>{r.count} {r.count === 1 ? 'deal' : 'deals'} · {pctv}% · {money(r.value)}</span>
                        </div>
                        <div style={{ height: '8px', borderRadius: '99px', background: 'var(--surface2)', overflow: 'hidden' }}>
                          <div style={{ width: `${pctv}%`, height: '100%', background: '#DC2626' }} />
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
